import React, { useEffect, useState } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';


interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional context prefilled into the message box, e.g. why the user landed here. */
  defaultMessage?: string;
  /** Optional heading override. */
  title?: string;
  subtitle?: string;
  /** Optional prefill for the "Enquiry type" select (individual/institutional/enterprise/support/other). */
  defaultInquirerType?: string;
}

interface ContactForm {
  first_name: string;
  last_name: string;
  email: string;
  organisation: string;
  inquirer_type: string;
  enquiry_type: string;
  message: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function splitName(fullName?: string): { first: string; last: string } {
  if (!fullName) return { first: '', last: '' };
  const parts = fullName.trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
}

function validateForm(form: ContactForm): Partial<Record<keyof ContactForm, string>> {
  const errors: Partial<Record<keyof ContactForm, string>> = {};
  if (!form.first_name.trim()) errors.first_name = 'First name is required';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required';
  if (!form.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = 'Enter a valid email address';
  }
  if (!form.inquirer_type) errors.inquirer_type = 'Please select an enquiry type';
  if (!form.message.trim()) {
    errors.message = 'Message is required';
  } else if (form.message.trim().length < 10) {
    errors.message = 'Message should be at least 10 characters';
  }
  return errors;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSalesModal({
  isOpen,
  onClose,
  defaultMessage,
  title = 'Get in touch',
  subtitle = "Tell us a bit about what you need — our sales team replies within one business day.",
  defaultInquirerType = 'enterprise',
}: ContactSalesModalProps) {
  const { user } = useAuth();
  const { first, last } = splitName(user?.full_name);

  const [form, setForm] = useState<ContactForm>({
    first_name: first,
    last_name: last,
    email: user?.email || '',
    organisation: user?.organisation || '',
    inquirer_type: defaultInquirerType,
    enquiry_type: 'sales',
    message: defaultMessage || '',
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Reset form state whenever the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setForm({
        first_name: first,
        last_name: last,
        email: user?.email || '',
        organisation: user?.organisation || '',
        inquirer_type: defaultInquirerType,
        enquiry_type: 'sales',
        message: defaultMessage || '',
      });
      setFieldErrors({});
      setStatus('idle');
      setErrorMsg('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof ContactForm]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof ContactForm];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setStatus('submitting');
    setErrorMsg('');

    try {
      await axios.post(
        `${BASE_URL}/contact/enquiry/`,
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          organisation: form.organisation.trim(),
          inquirer_type: form.inquirer_type,
          enquiry_type: form.enquiry_type,
          message: form.message.trim(),
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setStatus('success');
    } catch (err) {
      setStatus('error');
      if (axios.isAxiosError(err)) {
        setErrorMsg(err.response?.data?.message || err.response?.data?.error || err.message || 'Something went wrong. Please try again.');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    }
  };

  const inputClass = (field: keyof ContactForm) =>
    `w-full border rounded-lg px-3 py-2.5 text-[12px] text-gray-900 focus:outline-none ${fieldErrors[field] ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-blue-400'
    }`;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-[14px] font-extrabold text-gray-900">{title}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-3">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {status === 'success' ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={28} color="#22c55e" />
              </div>
              <p className="text-[14px] font-extrabold text-gray-900 mb-1">Message sent!</p>
              <p className="text-[12px] text-gray-500 mb-5">
                Thanks — our sales team will reach out to {form.email} shortly.
              </p>
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-navy text-white text-[12px] font-semibold rounded-lg hover:bg-navy-dark transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3.5">
              <p className="text-[13px] font-semibold text-gray-900">Send us a message</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">First name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    className={inputClass('first_name')}
                    placeholder="John"
                  />
                  {fieldErrors.first_name && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.first_name}</p>}
                </div>
                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Last name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    className={inputClass('last_name')}
                    placeholder="Smith"
                  />
                  {fieldErrors.last_name && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.last_name}</p>}
                </div>
              </div>

              <div>
                <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Email address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass('email')}
                  placeholder="john@university.edu"
                />
                {fieldErrors.email && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.email}</p>}
              </div>

              <div>
                <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Organisation</label>
                <input
                  type="text"
                  name="organisation"
                  value={form.organisation}
                  onChange={handleChange}
                  className={inputClass('organisation')}
                  placeholder="University or company name"
                />
              </div>

              <div>
                <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Enquiry type</label>
                <select
                  name="inquirer_type"
                  value={form.inquirer_type}
                  onChange={handleChange}
                  className={inputClass('inquirer_type')}
                >
                  <option value="">Select an option</option>
                  <option value="individual">Individual plan</option>
                  <option value="institution">Institutional plan</option>
                  <option value="publisher">Publisher plan</option>
                  <option value="support">Technical support</option>
                  <option value="other">Other</option>
                </select>
                {fieldErrors.inquirer_type && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.inquirer_type}</p>}
              </div>

              <div>
                <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className={`${inputClass('message')} resize-none`}
                  placeholder="Tell us how we can help..."
                />
                {fieldErrors.message && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.message}</p>}
              </div>

              {status === 'error' && <p className="text-[12px] text-red-600">{errorMsg}</p>}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3 bg-navy text-white text-[12px] font-semibold rounded-lg hover:bg-navy-dark transition-colors disabled:opacity-60"
              >
                {status === 'submitting' ? 'Sending...' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}